import { useRef, useState } from "react"
import { FieldState, Location, ManagementCompany, NewEmployee, Organization, User } from "../../Interfaces"
import useUser from "../../hooks/useUser"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { locationService } from "../../services/locationService"
import { ADMIN, AREA_MANAGER, ASSISTANT_MANAGER, COMPANY_MANAGER, MANAGER, OWNER, SUPER_USER } from "../../utils/roles"
import { cleanPhoneNumber, doPasswordsMatch, phoneNumberAutoFormat } from "../../utils/helper"
import CustomSelect from "./CustomSelect"
import Input from "./Input"
import ImageDragDrop from "./ImageDragDrop"
import { organizationService } from "../../services/organizationService"
import { managementCompanyService } from "../../services/managementCompanyService"
import { userService } from "../../services/userService"
import { MultiValue, SingleValue } from "react-select"
import { useNavigate } from "react-router-dom"
import SaveButtonWithStatus from "../general/SaveButtonWithStatus"

import validateInput from "../../utils/validate"
import { managerAssistantManagerFields, areaManagerFields, companyManagerOwnerAdminFields } from "../../constants/formFields"
import { useSiteTerminology } from "../../contexts/PublicSiteContext"

export default function AddUserForm({ fieldType, newEmployeeRole }: { fieldType: any[]; newEmployeeRole: number }) {
  const { id, roleId, managementCompanyId: currentUserManagementId } = useUser()
  const { managementCompanySingular, areaSingular, areaSingularIndefinite } = useSiteTerminology()
  const queryClient = useQueryClient()
  const navigate = useNavigate()


  // Get the appropriate field set based on role
  const getFieldSet = (role: number) => {
    if (role === MANAGER || role === ASSISTANT_MANAGER) {
      return managerAssistantManagerFields
    } else if (role === AREA_MANAGER) {
      return areaManagerFields
    } else if (role === COMPANY_MANAGER || role === OWNER || role === ADMIN) {
      return companyManagerOwnerAdminFields
    }
    return fieldType // fallback to original fields
  }

  const fields = getFieldSet(newEmployeeRole)
  let fieldsState: FieldState = {}
  // Set each input field to an empty string initially
  fields.forEach((field) => (fieldsState[field.name] = ""))

  // By using "enabled" we can control when the query is executed. It is used to prevent the query from running until the user has selected a role
  const { data: locations } = useQuery(["locations"], () => locationService.getAllByUser(id), { enabled: newEmployeeRole === MANAGER || newEmployeeRole === ASSISTANT_MANAGER })
  const { data: organizationList } = useQuery(["organizations"], () => organizationService.getAllByUser(id), { enabled: newEmployeeRole === AREA_MANAGER })
  const { data: managementCompanyList } = useQuery(["managementCompanies"], () => managementCompanyService.getAll(id), { enabled: true })

  const [addEmployeeStateForm, setAddEmployeeStateForm] = useState(fieldsState)
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [selectedLocationIds, setSelectedLocationsIds] = useState<number[]>([])
  const [selectecOrganizationIds, setSelectedOrganizationIds] = useState<number[]>([])
  const [selectedManagementCompanyId, setSelectedManagementCompanyId] = useState<number>(0)
  const [saveStatus, setSaveStatus] = useState<string | null>(null)
  const managementCompanyId = useRef<number | undefined>(undefined)
  const [fieldErrors, setFieldErrors] = useState<{ [field: string]: string }>({});

  // Set management company ID based on user role
  if (roleId === SUPER_USER) {
    // Super User can select management company
    managementCompanyId.current = selectedManagementCompanyId
  } else {
    // Non-Super User uses their own management company ID if no selection made
    managementCompanyId.current = selectedManagementCompanyId || currentUserManagementId
  }

  const handleInputChange = (e: {
    target: {
      validity: any
      setCustomValidity(arg0: string): unknown
      name: string
      value: string
    }
  }) => {
    const input = e.target
    if (input.name === "phone") {
      const phoneNumber = phoneNumberAutoFormat(input.value)
      validateInput(input, "Please match the pattern: 555-555-5555.")
      setAddEmployeeStateForm({ ...addEmployeeStateForm, [input.name]: phoneNumber })
    } else if (input.name === "pin") {
      // Only allow digits for PIN
      const digitsOnly = input.value.replace(/\D/g, '')
      setAddEmployeeStateForm({ ...addEmployeeStateForm, [input.name]: digitsOnly })
    } else {
      setAddEmployeeStateForm({ ...addEmployeeStateForm, [input.name]: input.value })
    }
  }

  const handleLocationSelection = (locations: MultiValue<Location>) => {
    const locationIds = locations.map((location): number => location.id!!)
    setSelectedLocationsIds(locationIds)
    if (locations[0]) {
      managementCompanyId.current = locations[0].managementCompanyId
    }
  }

  const handleOrganizationSelect = (organizations: MultiValue<Organization>) => {
    const organizationIds = organizations.map((organization): number => organization.id!!)
    setSelectedOrganizationIds(organizationIds)
    if (organizations[0]) {
      managementCompanyId.current = organizations[0].managementCompanyId
    }
  }

  const handleManagementCompanySelect = (managementCompany: SingleValue<ManagementCompany>) => {
    if (managementCompany) {
      setSelectedManagementCompanyId(managementCompany.id)
      managementCompanyId.current = managementCompany.id
    }
  }

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault()
    setFieldErrors({}); // Clear previous errors
    const isValid = isFormValid(addEmployeeStateForm)
    const newEmployee: NewEmployee = { ...addEmployeeStateForm, phone: cleanPhoneNumber(addEmployeeStateForm.phone || ""), managementCompanyId: managementCompanyId.current }

    console.log("addEmployeeStateForm:", addEmployeeStateForm)
    console.log("newEmployee being sent to API:", newEmployee)
    console.log("PIN value:", addEmployeeStateForm.pin)
    console.log("PIN type:", typeof addEmployeeStateForm.pin)
    console.log("Phone value:", addEmployeeStateForm.phone)
    console.log("Phone type:", typeof addEmployeeStateForm.phone)
    console.log("Selected location IDs:", selectedLocationIds)

    queryClient.invalidateQueries(["users"])
    let user: User | null = null
    if (isValid) {
      setSaveStatus("Saving...")
      try {
        if (newEmployeeRole === ASSISTANT_MANAGER) {
          newEmployee.password = newEmployee.password?.trim()
          delete newEmployee.confirmPassword
          user = await userService.createAssistantManager(newEmployee, selectedLocationIds, imageFile)
        } else if (newEmployeeRole === MANAGER) {
          newEmployee.password = newEmployee.password?.trim()
          delete newEmployee.confirmPassword
          user = await userService.createManager(newEmployee, selectedLocationIds, imageFile)
        } else if (newEmployeeRole === AREA_MANAGER) {
          newEmployee.password = newEmployee.password?.trim()
          delete newEmployee.confirmPassword
          user = await userService.createAreaManager(newEmployee, selectecOrganizationIds, imageFile)
        } else if (newEmployeeRole === COMPANY_MANAGER) {
          newEmployee.password = newEmployee.password?.trim()
          delete newEmployee.confirmPassword
          user = await userService.createCompanyManager(newEmployee, imageFile)
        } else if (newEmployeeRole === OWNER) {
          newEmployee.password = newEmployee.password?.trim()
          delete newEmployee.confirmPassword
          user = await userService.createOwner(newEmployee, imageFile)
        } else if (newEmployeeRole === ADMIN) {
          newEmployee.password = newEmployee.password?.trim()
          delete newEmployee.confirmPassword
          user = await userService.createAdmin(newEmployee, imageFile)
        }
      } catch (error: any) {
        // Parse the error message to extract field-specific errors
        const errorMessage = error.message || "An error occurred";
        const fieldErrors: { [field: string]: string } = {};

        // Parse error message like "email: Email is already in use by John Smith; pin: Pin is already in use by Jane Doe"
        const errorParts = errorMessage.split('; ');
        errorParts.forEach((part: string) => {
          const colonIndex = part.indexOf(': ');
          if (colonIndex > 0) {
            const field = part.substring(0, colonIndex);
            const message = part.substring(colonIndex + 2);
            fieldErrors[field] = message;
          }
        });

        setFieldErrors(fieldErrors);
        setSaveStatus("Error");
        setTimeout(() => setSaveStatus(null), 1500);
        return;
      }

      if (user) {
        setSaveStatus("Saved!")
        setTimeout(() => {
          setSaveStatus(null)
          if (user) navigate(`/app/users/${user.id}`, { state: { user: user } })
        }, 1000)
      } else {
        setSaveStatus("Error")
        setTimeout(() => {
          setSaveStatus(null)
        }, 1500)
      }
    }
  }

  const isFormValid = (addEmployeeStateForm: FieldState) => {
    // Check password match only if password fields exist
    if (addEmployeeStateForm.password && addEmployeeStateForm.confirmPassword) {
      const passwordMatch = doPasswordsMatch(addEmployeeStateForm.password, addEmployeeStateForm.confirmPassword)
      if (!passwordMatch) {
        alert("Passwords do not match")
        return false
      }
    }

    // Check if either email or phone is provided (for roles that require it)
    if (newEmployeeRole === AREA_MANAGER || newEmployeeRole === COMPANY_MANAGER || newEmployeeRole === OWNER || newEmployeeRole === ADMIN) {
      const hasEmail = addEmployeeStateForm.email && addEmployeeStateForm.email.trim() !== ""
      if (!hasEmail) {
        alert("Email is required for this role")
        return false
      }
    }

    // Check management company selection for all roles
    if (selectedManagementCompanyId === 0) {
      alert(`Please select a ${managementCompanySingular.toLowerCase()}`)
      return false
    }

    // Check organization selection for Area Managers
    if (newEmployeeRole === AREA_MANAGER) {
      if (selectecOrganizationIds?.length === 0) {
        alert(`Did you choose at least one ${areaSingular.toLowerCase()}?`)
        return false
      }
    }
    return true
  }

  return (
    <form className="mt-6 space-y-2" onSubmit={handleSubmit}>
      {/* Remove the general error display */}
      <div className="lg:flex lg:justify-evenly items-center">
        <section className="lg:w-full">
          <div>
            {/* Management Company Selection for All Users */}
            {!managementCompanyList?.length ? (
              <p className="text-red-500">Please add a {managementCompanySingular.toLowerCase()} first</p>
            ) : (
              <div className="mb-4">
                <CustomSelect list={managementCompanyList} handleSelection={handleManagementCompanySelect} placeholder={`Select ${managementCompanySingular}`} required={true} />
              </div>
            )}

            {/* Organization Selection for Area Managers */}
            {newEmployeeRole === AREA_MANAGER && (
              <>
                {!organizationList?.length ? (
                  <p className="text-red-500">Please add {areaSingularIndefinite} first</p>
                ) : (
                  <div className="mb-4">
                    <CustomSelect list={organizationList ? organizationList : []} handleSelection={handleOrganizationSelect} isMulti={true} placeholder={`Select ${areaSingular}`} required={true} />
                  </div>
                )}
              </>
            )}

            {/* Location Selection for Managers and Assistant Managers */}
            {(newEmployeeRole === MANAGER || newEmployeeRole === ASSISTANT_MANAGER) && (
              <>
                {!locations?.length ? (
                  <p className="text-red-500">Please add a location first</p>
                ) : (
                  <div className="p2 mb-4">
                    <CustomSelect list={locations ? locations : []} handleSelection={handleLocationSelection} isMulti={true} placeholder="Select Locations (optional)" />
                  </div>
                )}
              </>
            )}

            {fields?.map((field) => {
              return (
                <div key={field.id}>
                  <Input
                    className="bg-gray-50 border-gray-300"
                    handleChange={handleInputChange}
                    value={addEmployeeStateForm[field.id]}
                    labelText={field.labelText}
                    labelFor={field.labelFor}
                    id={field.id}
                    name={field.name}
                    type={field.type}
                    autoComplete={field.autoComplete}
                    isRequired={field.isRequired}
                    placeholder={field.placeholder}
                    pattern={field.pattern}
                    minLength={field.minLength}
                    maxLength={field.maxLength}
                  />
                  {fieldErrors[field.name] && (
                    <div className="text-red-500 text-sm mt-1">{fieldErrors[field.name]}</div>
                  )}
                </div>
              )
            })}
          </div>
          <div className="w-full"></div>
        </section>
        <section className="lg:w-full lg:pl-5 lg:m-auto">
          <ImageDragDrop setImageState={setImageFile} title="Upload Photo" />
        </section>
      </div>
      <SaveButtonWithStatus saveStatus={saveStatus} handleSave={handleSubmit} isForm={true} className="w-full" />
    </form>
  )
}
